/**
 * LA FICHE OUVERTE SUIT CE QUE LE SERVEUR ECRIT APRES ELLE.
 *
 * LE DEFAUT. Un jeu qui porte un champ AUTOMATIQUE (`calcul`, `numero`) est
 * le seul ou le serveur reecrit une fiche que l'ecran vient d'ecrire : le
 * declencheur des fiches (`notifierSurEcriture`) attribue le numero apres
 * « Creer », et refait un calcul apres une ecriture venue d'ailleurs (MCP,
 * Lia, flux, import). L'ecran relisait sa fiche juste apres SON ecriture,
 * avant le declencheur : la fiche restait sur « Non renseigne » et le numero
 * n'apparaissait qu'au rechargement (`docs/socle/champs-types.md`, section 4).
 *
 * LA PARADE. L'ecran s'abonne a la fiche ouverte par `observe` (le SDK, la
 * MEME ecoute pour toutes les familles) et pose chaque instantane retenu par
 * le chemin qui pose deja la fiche relue apres une ecriture (`useScreen`).
 * Ce module ne dit que LE CHOIX, pur, pour etre prouve sans React ni
 * Firestore : cet instantane remplace-t-il la fiche que l'ecran a en memoire ?
 *
 * POURQUOI SEULEMENT CES JEUX, et pas toutes les fiches : c'est le cas ou
 * l'ecran est faux sans rien faire de travers. Voir en direct le travail d'un
 * collegue sur n'importe quelle fiche est un autre chantier (le meme
 * abonnement, pose par toute porte), avec ses questions a lui : le cout d'une
 * ecoute par fiche ouverte, et ce qu'on montre quand deux personnes
 * modifient la meme case.
 */

/** Ce que l'ecoute vient de rendre pour la fiche suivie. */
export interface InstantaneDeFiche<T> {
  /** L'id ecoute. */
  id: string;
  /** La fiche validee par le converter de sa famille, ou `null` (absente, refusee). */
  record: T | null;
  /** L'echo d'une ecriture de CE client pas encore confirmee (`hasPendingWrites`). */
  local: boolean;
}

/** Ce que l'ecran a, au moment ou l'instantane arrive. */
export interface EtatDeLaFicheOuverte<T> {
  /** La fiche ouverte (ni brouillon, ni modele), ou `null`. */
  ouverteId: string | null;
  /** La fiche telle que l'ecran l'a chargee (le jeu, ou la fiche chargee seule). */
  enMemoire: T | undefined;
  /** Les ecritures de case parties et pas encore revenues. */
  ecrituresEnVol: number;
}

/**
 * CET INSTANTANE REMPLACE-T-IL LA FICHE ? Non, dans six cas, et chacun a sa
 * raison :
 *  - L'ECHO LOCAL : Firestore rejoue a l'abonne l'ecriture qu'il vient de
 *    faire, avant le serveur. L'ecran l'a deja (son optimisme), et le serveur
 *    n'y a encore rien ajoute ;
 *  - UNE AUTRE FICHE : l'ecoute d'avant peut livrer un dernier instantane
 *    entre le changement de fiche et son desabonnement ;
 *  - RIEN A REMPLACER : la fiche n'est pas (ou plus) dans ce que l'ecran a
 *    charge. L'abonnement ne la fait pas entrer : il suit, il ne charge pas ;
 *  - ABSENTE OU SUPPRIMEE : ce n'est pas a une ecoute de faire dire
 *    « introuvable » a la fiche ouverte. La navigation suivante fera foi,
 *    comme pour une fiche supprimee qu'on rouvre ;
 *  - UNE ECRITURE EN VOL : on IGNORE l'instantane, on ne l'attend pas. Le
 *    chemin d'ecriture relit lui-meme ce qu'il a ecrit en revenant (la porte
 *    en mode client, la fiche en mode serveur), et cette relecture part APRES
 *    l'instantane ignore : elle est au moins aussi fraiche. Le poser au
 *    milieu ferait passer la fiche sous une case dont l'optimisme et la pile
 *    d'annulation ne sont pas encore regles. Le declencheur, lui, ecrit une a
 *    deux secondes apres : son instantane arrive une fois l'ecriture revenue ;
 *  - LE MEME CONTENU : poser une fiche identique ferait repartir tout ce qui
 *    depend du jeu (stats, annuaires) pour rien.
 *
 * UNE CASE EN COURS D'EDITION NE BOUGE PAS, et ce n'est pas ce module qui le
 * tient : l'editeur garde sa saisie depuis son ouverture (`FieldEditorControl`,
 * `views/core/CellEditor.tsx`) et ne relit pas la fiche sous ses doigts.
 */
export function instantaneRemplaceLaFiche<T>(
  instantane: InstantaneDeFiche<T>,
  etat: EtatDeLaFicheOuverte<T>,
): instantane is InstantaneDeFiche<T> & { record: T } {
  if (instantane.local) return false;
  if (etat.ouverteId == null || instantane.id !== etat.ouverteId) return false;
  if (etat.enMemoire === undefined) return false;
  const record = instantane.record;
  if (record == null) return false;
  if ((record as { deletedAt?: unknown }).deletedAt != null) return false;
  if (etat.ecrituresEnVol > 0) return false;
  return !memeContenu(etat.enMemoire, record);
}

/**
 * DEUX FICHES DISENT-ELLES LA MEME CHOSE ? Comparaison de structure : une date
 * se compare a l'instant, un tableau case par case, un objet clef par clef. Une
 * clef absente et une clef `undefined` sont la meme absence (un converter peut
 * poser l'une ou l'autre).
 */
export function memeContenu(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((valeur, index) => memeContenu(valeur, b[index]));
  }
  const gauche = a as Record<string, unknown>;
  const droite = b as Record<string, unknown>;
  const clefs = new Set([...Object.keys(gauche), ...Object.keys(droite)]);
  for (const clef of clefs) {
    if (!memeContenu(gauche[clef], droite[clef])) return false;
  }
  return true;
}
